import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Mouse drag-select — click + drag over a container to pick items.
 *
 * Each selectable node must carry `data-select-key="<id>"`. The container ref
 * is what the user drags across. Selection is committed on mouseup; while
 * dragging the return `selected` set updates live so rows can highlight.
 *
 * Modifier keys:
 *   - plain drag: replace selection with items under the marquee
 *   - Shift  drag: additive — merge with existing selection
 *   - Ctrl/⌘ drag: toggle — items already selected are removed
 *
 * Click on a single row toggles it. Escape clears the set.
 */
export function useDragSelect(containerRef) {
  const [selected, setSelected] = useState(() => new Set());
  const [dragging, setDragging] = useState(false);
  const [rect, setRect] = useState(null);

  const startRef = useRef(null);
  const modeRef = useRef("replace");   // replace | add | toggle
  const baseRef = useRef(new Set());   // selection before drag started

  const nodesUnder = useCallback((r) => {
    const c = containerRef.current;
    if (!c) return new Set();
    const hits = new Set();
    const nodes = c.querySelectorAll("[data-select-key]");
    nodes.forEach((n) => {
      const b = n.getBoundingClientRect();
      // AABB intersection with the marquee rect
      if (b.right < r.left || b.left > r.right || b.bottom < r.top || b.top > r.bottom) return;
      hits.add(n.getAttribute("data-select-key"));
    });
    return hits;
  }, [containerRef]);

  const applyMode = useCallback((hits) => {
    const base = baseRef.current;
    if (modeRef.current === "add") {
      const merged = new Set(base);
      hits.forEach((h) => merged.add(h));
      return merged;
    }
    if (modeRef.current === "toggle") {
      const merged = new Set(base);
      hits.forEach((h) => { if (merged.has(h)) merged.delete(h); else merged.add(h); });
      return merged;
    }
    return new Set(hits);
  }, []);

  useEffect(() => {
    const c = containerRef.current;
    if (!c) return undefined;

    const onDown = (e) => {
      // Only left mouse button; ignore clicks that started inside an anchor/button/input.
      if (e.button !== 0) return;
      const t = e.target;
      if (t.closest && t.closest("a, button, input, textarea, select, [data-noselect]")) return;
      startRef.current = { x: e.clientX, y: e.clientY };
      modeRef.current = e.shiftKey ? "add" : (e.ctrlKey || e.metaKey ? "toggle" : "replace");
      baseRef.current = new Set(selected);
      // Don't immediately setDragging — wait for movement > 4px so plain clicks still work.
    };

    const onMove = (e) => {
      const s = startRef.current;
      if (!s) return;
      const dx = e.clientX - s.x;
      const dy = e.clientY - s.y;
      if (!dragging && Math.abs(dx) + Math.abs(dy) < 5) return;
      if (!dragging) setDragging(true);
      const r = {
        left: Math.min(s.x, e.clientX),
        right: Math.max(s.x, e.clientX),
        top: Math.min(s.y, e.clientY),
        bottom: Math.max(s.y, e.clientY),
      };
      setRect(r);
      setSelected(applyMode(nodesUnder(r)));
    };

    const onUp = () => {
      startRef.current = null;
      if (dragging) { setDragging(false); setRect(null); }
    };

    const onKey = (e) => { if (e.key === "Escape") setSelected(new Set()); };

    c.addEventListener("mousedown", onDown);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("keydown", onKey);
    return () => {
      c.removeEventListener("mousedown", onDown);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("keydown", onKey);
    };
  }, [containerRef, dragging, selected, nodesUnder, applyMode]);

  const toggle = useCallback((key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected(new Set()), []);
  const selectAll = useCallback((keys) => setSelected(new Set(keys)), []);

  return { selected, dragging, rect, toggle, clear, selectAll };
}
